import { SET_THEME } from '@Common/models/redux/actions/names';
import React from 'react';
import { connect as coreConnect, Provider, useSelector } from 'react-redux';
import { createStore, Store, applyMiddleware } from 'redux';
import { CREATE_USER } from 'src/authentication/models/action';
import { CoreThemeProvider } from 'src/theme';
import reducers from './reducers';
import thunkMiddleware from 'redux-thunk';
import { getConfig } from 'next/config';
import {
  setABExperiments,
  setAppLangauge,
  setCampaignAction,
  setCountryCode,
  setExtensionAction,
  setIndependentTool,
  setLanguageAction,
  setPartnerCompany,
  setPlatformAction,
  setSidebarStateChange,
  setTheme,
} from '@Common/actions/commonActions';
import { detectAgent, detectLanguage, getCampaign, NativePlatform } from 'core';
import { createUserAction } from 'src/authentication/actions/authenticationActions';
import { ExtensionMetaData } from '@Common/models/redux/store';
import { composeWithDevTools } from 'redux-devtools-extension';
import { Request } from 'express';

declare const window: any;

const { publicRuntimeConfig } = getConfig();
let coreStore: Store;

/**
 * dynamicMiddlewares is used to dynamically add module specific middlewares.
 * Check for addMiddleware in code to see how to add dynamic middleware.
 */
const bindMiddleware = () => {
  let middlewares;
  if (process.browser) {
    // TODO:: middlewares = applyMiddleware(thunkMiddleware, dynamicMiddlewares);
    // dynamicMiddlewares is throwing error on adding
    middlewares = applyMiddleware(thunkMiddleware);
  } else {
    middlewares = applyMiddleware(thunkMiddleware);
  }

  if (publicRuntimeConfig.QUILLBOT_ENV !== 'prod') {
    const composeEnhancers = composeWithDevTools({ name: 'core' });
    return composeEnhancers(middlewares);
  }

  return middlewares;
};

const CoreProvider = (props: { req?: Request; emotionCache: any; children: React.ReactElement }) => {
  const { emotionCache, req } = props;

  createCoreStore({ req });
  return (
    <Provider store={coreStore}>
      <CoreThemeProvider emotionCache={emotionCache}>{props.children}</CoreThemeProvider>
    </Provider>
  );
};

const getCoreStore = () => {
  return {
    dispatch: coreDispatch,
    getState: () => coreStore.getState(),
  };
};

const createCoreStore = ({ req }: { req?: Request }) => {
  if (typeof window === 'undefined' && req) {
    const { cookies, headers } = req;

    const sidebarPinned = cookies?.sidebarPinned === 'true';

    coreStore = createStore(reducers, bindMiddleware());
    coreDispatch(setSidebarStateChange(sidebarPinned));
    coreDispatch({ type: SET_THEME, payload: cookies?.theme || 'default' });
    const campaign = getCampaign(req);
    if (campaign) coreDispatch(setCampaignAction(campaign));
    coreDispatch({
      type: CREATE_USER,
      payload: {
        isAuthenticated: cookies?.authenticated == 'true',
        premium: cookies?.premium == 'true',
      },
    });
    coreDispatch(
      setABExperiments({
        abExperiments: cookies?.abExperiments ? JSON.parse(cookies?.abExperiments) : null,
        abExperimentsInfo: cookies?.abExperimentsInfo ? JSON.parse(cookies?.abExperimentsInfo) : null,
      })
    );

    if (req.baseUrl === '/extension') {
      let extensionType: ExtensionMetaData['type'] = 'chrome';
      if (req.query) {
        if (req.query.wordExt === '') {
          if (req.query.msWord === '') extensionType = 'ms-word';
          else extensionType = 'google-docs';
        }
      }

      coreDispatch(setExtensionAction({ activated: true, type: extensionType }));
    }

    let partnerCompany = null;
    if (req.baseUrl === '/grammar-check' || req.baseUrl === '/citation-generator' || req.baseUrl === '') {
      if (req?.query?.independentTool === 'true') {
        coreDispatch(setIndependentTool(true));
        if (req?.query?.partnerCompany) {
          partnerCompany = req?.query?.partnerCompany;
        }
        if (req?.query?.language) {
          const language = req?.query?.language as string;
          if (language) {
            coreDispatch(setAppLangauge(language));
          }
        }
      }
    }

    let countryCode = headers['cf-ipcountry'] || '';
    countryCode = Array.isArray(countryCode) ? countryCode[0] : countryCode;
    const validNativeQuery = Object.values(NativePlatform);
    const agent = detectAgent({ req: { headers } });
    const lang = detectLanguage({ req: { headers } });
    let savedTheme = cookies?.theme || 'default';
    if (partnerCompany) {
      savedTheme = partnerCompany as string;
      coreDispatch(setPartnerCompany(partnerCompany as string));
    }
    const nativeQuery = req.query?.native as NativePlatform;
    let native = req.baseUrl === '/native' ? NativePlatform.ANDROID : undefined;
    if (!native && req.query?.native && validNativeQuery.includes(nativeQuery)) {
      native = nativeQuery;
    }
    coreDispatch(
      setPlatformAction({
        ...agent,
        native,
      })
    );
    coreDispatch(setLanguageAction(lang));
    const authState = cookies?.authenticated === 'true';
    const premiumState = cookies?.premium === 'true';
    // const userOnboardingStatus = cookies?.userOnboardingScenario !== 'false';

    coreDispatch(createUserAction({ isAuthenticated: authState, premium: premiumState }));
    // coreDispatch(setUserOnboardingFromCookie({ userOnboardingStatus }));
    coreDispatch(setTheme(savedTheme));
    coreDispatch(setCountryCode(countryCode));
  } else {
    coreStore = createStore(reducers, window.__QUILLBOT_CORE_STATE__ || undefined, bindMiddleware());
    delete window.__QUILLBOT_CORE_STATE__;
  }
  return getCoreStore();
};

const coreDispatch = (data: any) => {
  coreStore.dispatch(data);
};

const useCoreSelector = useSelector;
const getReduxStore = getCoreStore;

export { CoreProvider, coreDispatch, useCoreSelector, getCoreStore, getReduxStore, coreConnect };
